import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ScanLine,
  FileText,
  Send,
  CheckCircle2,
  ArrowUpRight,
  Sparkles,
  Circle,
} from "lucide-react";

const STEPS = [
  {
    key: "scan",
    n: 1,
    icon: ScanLine,
    title: "Scan your credit report",
    body: "Upload a PDF from annualcreditreport.com (or paste text). Claude finds every disputable item in ~30 seconds.",
    cta: "Start AI scan",
    route: "/app/scan",
    helper: "Don't have your report yet?",
    helperRoute: "/app/get-report",
    helperCta: "6 free sources →",
  },
  {
    key: "dispute",
    n: 2,
    icon: FileText,
    title: "Draft your first dispute letter",
    body: "Pick one negative item and let Aura draft a bureau-grade letter with FCRA citations. Read it, tweak it if you want.",
    cta: "Go to negative items",
    route: "/app/items",
  },
  {
    key: "mail",
    n: 3,
    icon: Send,
    title: "Send it as USPS Certified Mail",
    body: "Aura prints, folds, stamps, and mails your letter — you get a tracking number instantly. Your Basic plan includes 5/mo free.",
    cta: "Open a dispute to send",
    route: "/app/disputes",
  },
];

/**
 * OnboardingWizard reads the user snapshot from /api/tips response
 * and shows a 3-step progress tracker + the active-step CTA card.
 * When all 3 done -> returns null so the ranked-tips list can show instead.
 */
export function OnboardingWizard({ snapshot }) {
  const s = snapshot || {};
  const disp = s.disputes_by_status || {};
  const scanDone = (s.negative_items || 0) > 0;
  const disputeDone = (disp.draft || 0) + (disp.sent || 0) + (disp.delivered || 0) + (disp.resolved || 0) > 0;
  const mailDone = (disp.sent || 0) + (disp.delivered || 0) + (disp.resolved || 0) > 0
    || (s.certified_mail_delivered || 0) > 0;

  const state = { scan: scanDone, dispute: disputeDone, mail: mailDone };
  const nextIdx = STEPS.findIndex((step) => !state[step.key]);
  if (nextIdx === -1) return null;
  const active = STEPS[nextIdx];
  const ActiveIcon = active.icon;

  return (
    <div className="mb-12" data-testid="onboarding-wizard">
      {/* Header + progress */}
      <div className="card-surface p-6 mb-4">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 border border-[#D5FF3F]/30 bg-[#D5FF3F]/5 rounded-full px-3 py-1 mb-3">
              <Sparkles size={11} className="text-[#D5FF3F]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#D5FF3F]">
                First-time setup · {nextIdx + 1} of 3
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Let&rsquo;s get your first
              <br />
              <span className="text-[#D5FF3F]">win on the board.</span>
            </h2>
            <p className="text-sm text-[#A8ADA5] mt-2 max-w-lg">
              Three quick moves. Once you&rsquo;ve sent your first certified letter, the AI Coach
              takes over with your personalized top-3 tactics.
            </p>
          </div>
          <div className="ticker text-5xl text-[#D5FF3F]" data-testid="onboarding-progress">
            {Object.values(state).filter(Boolean).length}/3
          </div>
        </div>

        {/* Step chips */}
        <div className="grid grid-cols-3 gap-3">
          {STEPS.map((step, i) => {
            const done = state[step.key];
            const isActive = i === nextIdx;
            const Icon = step.icon;
            return (
              <div
                key={step.key}
                data-testid={`onboarding-step-chip-${step.key}`}
                className={`p-3 rounded-sm border transition-colors ${
                  done
                    ? "border-[#7CFF9B]/30 bg-[#7CFF9B]/5"
                    : isActive
                    ? "border-[#D5FF3F]/40 bg-[#D5FF3F]/5"
                    : "border-[#26282C] bg-[#131518]"
                }`}
              >
                <div className="flex items-center gap-2 mb-1.5">
                  {done ? (
                    <CheckCircle2 size={14} className="text-[#7CFF9B]" />
                  ) : (
                    <Circle
                      size={14}
                      className={isActive ? "text-[#D5FF3F]" : "text-[#6D716C]"}
                    />
                  )}
                  <span className="ticker text-xs text-[#6D716C]">0{step.n}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon
                    size={12}
                    className={done ? "text-[#7CFF9B]" : isActive ? "text-[#D5FF3F]" : "text-[#6D716C]"}
                  />
                  <div
                    className={`text-xs font-medium leading-tight ${
                      done
                        ? "text-[#7CFF9B] line-through decoration-[#7CFF9B]/40"
                        : isActive
                        ? "text-[#F5F3EE]"
                        : "text-[#6D716C]"
                    }`}
                  >
                    {step.title.replace("your ", "").replace(" first ", " ")}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Active step card */}
      <div
        className="card-surface p-8 lg:p-10 border-[#D5FF3F]/40 bg-gradient-to-br from-[#D5FF3F]/10 to-transparent relative overflow-hidden"
        data-testid={`onboarding-active-${active.key}`}
      >
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#D5FF3F]/15 blur-[100px] pointer-events-none" />
        <div className="relative flex items-start gap-5 flex-wrap">
          <div className="w-14 h-14 rounded-sm bg-[#D5FF3F] flex items-center justify-center flex-shrink-0">
            <ActiveIcon size={22} strokeWidth={2} className="text-[#0B0C0E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="overline mb-1.5 text-[#D5FF3F]">Step {active.n} of 3</div>
            <h3 className="font-display text-3xl md:text-4xl leading-tight mb-3">
              {active.title}
            </h3>
            <p className="text-[#A8ADA5] leading-relaxed mb-5 max-w-2xl">{active.body}</p>
            <div className="flex flex-wrap gap-3">
              <Link to={active.route}>
                <Button
                  data-testid={`onboarding-cta-${active.key}`}
                  className="rounded-full btn-lime h-12 px-6 text-base"
                >
                  {active.cta} <ArrowUpRight size={16} className="ml-1.5" />
                </Button>
              </Link>
              {active.helper && active.helperRoute && (
                <Link to={active.helperRoute}>
                  <Button className="rounded-full btn-outline h-12 px-5">
                    {active.helper}{" "}
                    <span className="text-[#D5FF3F] ml-2 font-mono text-xs">
                      {active.helperCta}
                    </span>
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Skip note */}
      <div className="text-center mt-4">
        <span className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest">
          Prefer to explore first? Every page is unlocked.
        </span>
      </div>
    </div>
  );
}
